// Searchable league picker — 276 leagues is unusable as a plain list, so this
// is a type-to-filter dropdown showing each league's country flag + a men's/
// women's tag (mirrors Catalyst's CompetitionSelector). Data from /competitions.

import { useMemo, useRef, useState, useEffect } from "react";
import type { CompetitionSummary } from "@catbento/shared";
import { countryFlag } from "../lib/countryFlag.js";
import { theme } from "../lib/theme.js";

function label(c: CompetitionSummary): string {
  const flag = countryFlag(c.countryCode);
  return `${flag ? flag + " " : ""}${c.name}`;
}

export default function LeagueSelect({
  competitions,
  selectedId,
  onSelect,
  loading,
}: {
  competitions: CompetitionSummary[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  loading: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef<HTMLDivElement>(null);

  // Close on an outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selected = competitions.find((c) => c.id === selectedId) ?? null;

  // Group by COUNTRY, then league name within a country — so all of a country's
  // leagues sit together (matches old Catalyst, and how the /competitions endpoint
  // already orders them). Supranational comps (no country, e.g. Champions League)
  // fall to the end. This also fixes the earlier bug where the list was unsorted
  // and capped at 100 rows, hiding leagues like MLS until you typed.
  const sorted = useMemo(() => {
    // "￿" sorts after any real name → nulls (supranational) go last.
    const countryKey = (c: CompetitionSummary) => c.countryName ?? "￿";
    return [...competitions].sort((a, b) => {
      const byCountry = countryKey(a).localeCompare(countryKey(b));
      return byCountry !== 0 ? byCountry : a.name.localeCompare(b.name);
    });
  }, [competitions]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? sorted.filter((c) => c.name.toLowerCase().includes(q)) : sorted;
  }, [sorted, query]);

  return (
    <div ref={boxRef} style={{ position: "relative", minWidth: 240 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={loading}
        style={{
          width: "100%",
          textAlign: "left",
          background: theme.input,
          color: theme.ink,
          border: `1px solid ${theme.borderInput}`,
          borderRadius: 8,
          padding: "8px 12px",
          fontSize: 14,
          cursor: loading ? "default" : "pointer",
          display: "flex",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {loading ? "Loading leagues…" : selected ? label(selected) : "Select a league"}
        </span>
        <span style={{ color: theme.inkMuted }}>▾</span>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 20,
            background: theme.tile,
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            overflow: "hidden",
          }}
        >
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search leagues…"
            style={{
              width: "100%",
              boxSizing: "border-box",
              background: theme.field,
              color: theme.ink,
              border: "none",
              borderBottom: `1px solid ${theme.border}`,
              padding: "9px 12px",
              fontSize: 13,
              outline: "none",
            }}
          />
          <div style={{ maxHeight: 280, overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "10px 12px", color: theme.inkFaint, fontSize: 13 }}>No matches.</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    onSelect(c.id);
                    setOpen(false);
                    setQuery("");
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    background: c.id === selectedId ? theme.accentSurface : "transparent",
                    color: theme.ink,
                    border: "none",
                    padding: "8px 12px",
                    fontSize: 13,
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {label(c)}
                  </span>
                  {c.competitionWomens ? (
                    <span style={{ color: theme.accent, fontSize: 11, flexShrink: 0 }}>W</span>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
